import React from "react";
import {
  PeopleReportHistory,
  PhoneReportHistory,
  PropertyReportHistory,
  SocialReportHistory,
  PullPhoneReportParams,
  VehicleReportHistory,
  NeighborhoodReportHistory,
} from "../../../features/reports/reportTypes";
import {
  ContainerLineItem,
  Icon,
  IconList,
  Layout,
  LoadingPlaceholder,
  Padding,
  ReportLink,
  Tag,
  Text,
} from "@kidslivesafe/sc-react";
import {
  PullPeopleReportParams,
  usePeopleReportDispatch,
} from "../../../hooks/peopleReportHook";

import { usePhoneReportDispatch } from "../../../hooks/phoneReportHook";

import {
  PullPropertyReportParams,
  usePropertySearchDispatch,
} from "../../../hooks/propertyReportHook";

import {
  formatAddress,
  formatName,
  formatPhoneNumber,
  fullCapsText,
  generatePeopleReportHighlights,
  parseDate,
  generatePhoneReportHighlights,
  generateSocialReportHighlights,
  renderSocialReportType,
  generatePropertyReportHighlights,
  generateNeighborhoodReportHighlights,
} from "../../../utils";
import {
  generateList,
  useCurrentBreakpoint,
} from "@kidslivesafe/sc-foundation";
import { useSocialSearchDispatch } from "../../../hooks/socialSearchHook";
import { useVehicleReportDispatch } from "../../../hooks/vehicleReportHook";
import { useNeighborhoodReportDispatch } from "../../../hooks/neighborhoodReportHook";
import { useAppSelector } from "../../../hooks/hooks";
import { selectBrandInfo } from "../../../features/brand/brandSlice";

interface ActivityHistoryLineItemProps {
  report:
    | PeopleReportHistory
    | PhoneReportHistory
    | PropertyReportHistory
    | SocialReportHistory
    | VehicleReportHistory
    | NeighborhoodReportHistory
    | undefined;
  loading?: boolean;
  loadingKey?: any;
}

const ActivityHistoryLineItem: React.FC<ActivityHistoryLineItemProps> = ({
  report,
  loading = false,
  loadingKey,
}) => {
  const pullPeopleReport = usePeopleReportDispatch();
  const searchSocial = useSocialSearchDispatch();
  const pullPhoneReport = usePhoneReportDispatch();
  const pullPropertyReport = usePropertySearchDispatch();
  const pullVehicleReport = useVehicleReportDispatch();
  const pullNeighborhoodReport = useNeighborhoodReportDispatch();
  const brandInfo = useAppSelector(selectBrandInfo);

  const currentBreakpoint = useCurrentBreakpoint();

  const detailsSpacing = currentBreakpoint === "desktop" ? "md" : "xxs";
  // helper functions.

  // render history based on report type
  const renderHistory = (
    report:
      | PeopleReportHistory
      | PhoneReportHistory
      | PropertyReportHistory
      | SocialReportHistory
      | VehicleReportHistory
      | NeighborhoodReportHistory
      | undefined,
  ) => {
    if (!report) return null;
    switch (report.type) {
      case "people":
        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <Layout
                type="flex"
                flexProps={{ gap: "xxs", alignItems: "center" }}
              >
                <ReportLink<PullPeopleReportParams>
                  noWrap
                  handleClick={() =>
                    pullPeopleReport(report.reportToken, "activity history")
                  }
                  inputValue={report.reportToken}
                  text={formatName(report)}
                />
                {report.dateOfDeath ? (
                  <Tag type="deceased" tagText="deceased" />
                ) : Number(report.age) >= 100 ? (
                  <Tag type="deceased" tagText=" presumed deceased" />
                ) : null}
              </Layout>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconPeople} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={
              report.schema === "v2"
                ? generatePeopleReportHighlights(report)
                : "null"
            }
          />
        );
      case "property":
        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <ReportLink<PullPropertyReportParams>
                noWrap
                text={fullCapsText(formatAddress(report, "with-comma"))}
                inputValue={{
                  addressLine1: report.addressLine1,
                  addressLine2: report.addressLine2,
                  state: report.state,
                  city: report.city,
                  zip: report.zip,
                  lat: report.latitude,
                  long: report.longitude,
                  propertyId: report.propertyId,
                  nearbySchoolRadius: 5,
                  searchMethod: "LinkClick",
                }}
                handleClick={inputValue => {
                  pullPropertyReport(inputValue, "activity history");
                }}
              />
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconProperty} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={
              report.schema !== undefined
                ? generatePropertyReportHighlights(report)
                : null
            }
          />
        );
      case "social":
        const socialText = report.email
          ? report.email
          : report.twitter
            ? report.twitter
            : report.phone
              ? formatPhoneNumber(report.phone)
              : "";

        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <ReportLink
                noWrap
                inputValue={socialText}
                text={fullCapsText(socialText)}
                handleClick={inputValue => {
                  searchSocial(inputValue, "activity history");
                }}
              />
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconSocial} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={
              report.schema === "v2"
                ? `${generateSocialReportHighlights(
                    report,
                    renderSocialReportType(report),
                  )}`
                : null
            }
          />
        );
      case "phone":
        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <ReportLink<PullPhoneReportParams>
                noWrap
                inputValue={report.phone}
                text={formatPhoneNumber(report.phone)}
                handleClick={inputValue => {
                  pullPhoneReport(inputValue, "activity history");
                }}
              />
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconPhone} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={
              report.schema === "v2"
                ? generatePhoneReportHighlights(report, false)
                : null
            }
          />
        );
      case "vehicle":
        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <ReportLink
                noWrap
                inputValue={{
                  vin: report.vin,
                  plate: report.plateNumber,
                  state: report.plateState,
                }}
                text={
                  report.vehicleDescription
                    ? report.vehicleDescription.toUpperCase()
                    : report.vin
                }
                handleClick={inputValue => {
                  pullVehicleReport(inputValue, "activity history");
                }}
              />
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconVehicle} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={generateList(
              [
                {
                  value: report.vehicleDescription ? report.plateNumber : "",
                  label: "Plate ",
                },
                {
                  value: report.vehicleDescription ? report.vin : "",
                  label: "",
                },
                {
                  value: report.titleCount
                    ? `${report.titleCount} Title Record${
                        report.titleCount === 1 ? "" : "s"
                      }`
                    : null,
                  label: "",
                },
              ],
              " | ",
            )}
          />
        );

      case "neighborhood":
        return (
          <ContainerLineItem
            key={report.score}
            lineItemHeader={
              <ReportLink
                noWrap
                inputValue={{
                  addressSearch: {
                    address: report.addressLine1,
                    addressLine2: report.addressLine2,
                    city: report.city,
                    state: report.state,
                    zipCode: report.zip,
                  },
                  searchMethod: "address",
                }}
                text={fullCapsText(formatAddress(report))}
                handleClick={pullNeighborhoodReport}
              />
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconNeighborhoodWatch} />
                <div className="activity-history__line-item-details">
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      direction: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {parseDate(report.score, "down-to-the-minute")
                      .split(",")
                      .map((date, index) => {
                        return (
                          <Text size="xxs" key={index}>
                            {date}
                          </Text>
                        );
                      })}
                  </Layout>
                </div>
              </Layout>
            }
            lineItemSubHeader={
              report.schema === "v2"
                ? generateNeighborhoodReportHighlights(report)
                : null
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading ? (
        <ContainerLineItem
          key={loadingKey}
          lineItemHeader={
            <LoadingPlaceholder width={"10rem"} height={"20px"} />
          }
          lineItemDetails={
            <Layout
              type="flex"
              flexProps={{ gap: detailsSpacing, alignItems: "center" }}
            >
              <LoadingPlaceholder width={"32px"} height={"32px"} />
              <div className="activity-history__line-item-details">
                <Layout
                  type="flex"
                  flexProps={{
                    gap: "xxs",
                    direction: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <LoadingPlaceholder width={"55px"} height={"13px"} />
                  <LoadingPlaceholder width={"50px"} height={"13px"} />
                </Layout>
              </div>
            </Layout>
          }
          lineItemSubHeader={<LoadingPlaceholder width={"12rem"} />}
        />
      ) : (
        renderHistory(report)
      )}
    </>
  );
};

export default ActivityHistoryLineItem;
